import { ReactPlugin, withAITracking } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

import { connectionString } from './connectionString.js';

export const reactPlugin = new ReactPlugin();

let appInsights = null;

// React hydration errors (#418/#423) and many caught errors only ever reach
// console.error — they never hit window.onerror, so the SDK's auto exception
// tracking misses them. Mirror console.error into App Insights as exceptions
// (with the real Error's stack when present) so they're queryable. Dedup per
// message within a short window so a render loop can't flood ingestion.
function installConsoleErrorCapture(ai) {
  if (typeof window === 'undefined' || !ai || console.error.__aiPatched) return;
  const original = console.error;
  const seen = new Map();
  const WINDOW_MS = 5000;

  const patched = function (...args) {
    try {
      const msg = args
        .map((a) => (a instanceof Error ? a.message : typeof a === 'string' ? a : ''))
        .join(' ')
        .trim()
        .slice(0, 300);
      // Skip React dev warnings ("Warning: ...") — only meaningful in dev, noisy otherwise.
      if (msg && !msg.startsWith('Warning:')) {
        const now = Date.now();
        if (now - (seen.get(msg) || 0) > WINDOW_MS) {
          if (seen.size > 200) seen.clear();
          seen.set(msg, now);
          const err = args.find((a) => a instanceof Error) || new Error(msg);
          ai.trackException({ exception: err, severityLevel: 3, properties: { source: 'console.error', detail: msg } });
        }
      }
    } catch {
      /* never let telemetry break the app */
    }
    original.apply(console, args);
  };
  patched.__aiPatched = true;
  console.error = patched;
}

export function initAppInsights({ history } = {}) {
  if (appInsights) return appInsights;

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true,
      enableUnhandledPromiseRejectionTracking: true,
      autoTrackPageVisitTime: false,
      enableAjaxErrorStatusText: true,
      // Don't inject `Request-Id` / `traceparent` headers into cross-origin
      // requests — third-party APIs (Giphy, Unsplash, etc.) don't whitelist
      // them in CORS, so the browser blocks the preflight.
      enableCorsCorrelation: false,
      disableCorrelationHeaders: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      extensions: [reactPlugin],
      extensionConfig: {
        [reactPlugin.identifier]: { history },
      },
    },
  });

  appInsights.loadAppInsights();

  // Drop telemetry that targets App Insights itself — prevents feedback loops
  // when the ingest endpoint fails and the SDK turns those failures into new
  // exceptions/dependencies, which would be sent again, ad infinitum.
  appInsights.addTelemetryInitializer((envelope) => {
    const data = envelope?.baseData || {};
    const url = data.target || data.uri || data.name || '';
    if (typeof url === 'string' && url.includes('applicationinsights.azure.com')) {
      return false;
    }
    return true;
  });

  appInsights.trackPageView();

  installConsoleErrorCapture(appInsights);

  return appInsights;
}

export const getAppInsights = () => appInsights;
export { withAITracking };
